import React, { useEffect, useRef, useCallback } from 'react'
import { Layout, Modal } from 'antd'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import Header from './Header'
import { clearUser, setLoggingOut, logout as logoutThunk } from '../features/auth/authSlice'

const { Content, Footer } = Layout

// Security Finding 8.5 - Session inactivity timeout
// Application must auto-logout after configured minutes of inactivity (default 60).
// Configured via environment variables:
//   VITE_SESSION_TIMEOUT_MINUTES (default: 60)
//   VITE_SESSION_WARNING_SECONDS (default: 60)
const SESSION_TIMEOUT_MINUTES = Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES) || 60
const SESSION_WARNING_SECONDS = Number(import.meta.env.VITE_SESSION_WARNING_SECONDS) || 60
const IDLE_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000
const WARNING_BEFORE_MS = SESSION_WARNING_SECONDS * 1000

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
  'wheel',
]

const MainLayout = ({ children }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { instance } = useMsal()
  const idleTimerRef = useRef(null)
  const warningTimerRef = useRef(null)
  const warningModalRef = useRef(null)

  const performLogout = useCallback(async () => {
    // Show full-screen overlay before any state changes so no intermediate
    // route is rendered between idle-timeout firing and AAD redirect.
    dispatch(setLoggingOut(true))
    try {
      // Security Finding 8.5/8.6 - Use the central logout thunk so backend
      // /api/Login/LogOutUser is invoked, server-side TokenSession is removed,
      // storage is wiped, then MSAL logoutRedirect happens last.
      await dispatch(logoutThunk()).unwrap()
    } catch (err) {
      console.error('Idle auto-logout failed:', err)
      // Fallback - clear locally and go to logout page
      dispatch(clearUser())
      dispatch(setLoggingOut(false))
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch (_) {
        /* no-op */
      }
      navigate('/logout', { replace: true })
    }
  }, [dispatch, instance, navigate])

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (warningModalRef.current) {
      warningModalRef.current.destroy()
      warningModalRef.current = null
    }

    // Schedule warning shortly before timeout
    warningTimerRef.current = setTimeout(() => {
      warningModalRef.current = Modal.warning({
        title: 'Session about to expire',
        content:
          'You have been inactive. For security reasons, you will be signed out shortly. Move the mouse or press a key to stay signed in.',
        okText: 'Stay signed in',
        onOk: () => {
          if (warningModalRef.current) {
            warningModalRef.current.destroy()
            warningModalRef.current = null
          }
          resetIdleTimer()
        },
      })
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS)

    // Schedule actual logout
    idleTimerRef.current = setTimeout(() => {
      performLogout()
    }, IDLE_TIMEOUT_MS)
  }, [performLogout])

  useEffect(() => {
    // Start the idle timer on mount
    resetIdleTimer()

    const handleActivity = () => {
      // Do not reset while warning modal is shown - user must explicitly confirm
      if (warningModalRef.current) return
      resetIdleTimer()
    }

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    )

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      )
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
      if (warningModalRef.current) {
        warningModalRef.current.destroy()
        warningModalRef.current = null
      }
    }
  }, [resetIdleTimer])

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Content style={{ padding: '24px', paddingBottom: '50px', background: '#fff', flex: 1 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>{children}</div>
      </Content>
      <Footer
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          background: '#f5f5f5',
          color: '#666',
          padding: '8px 24px',
          zIndex: 1000,
          borderTop: '1px solid #e0e0e0',
          fontSize: '12px'
        }}
      >
        Application Tracker ©{new Date().getFullYear()} | Version 1.0
      </Footer>
    </Layout>
  )
}

export default MainLayout
